/**
 * Pharmakon — modal focus-trap hook (EG-16 / B2G A11Y-5b).
 *
 * Ported from web-medecin's hooks/useFocusTrap.js (the EG-16 reference
 * implementation; novanalyse ui/Modal.tsx carries the same behaviour): while a
 * modal is open, keyboard focus is CONFINED to it (Tab / Shift+Tab cycle
 * within, they never escape to the page behind the scrim), initial focus lands
 * inside the modal, and on close focus RETURNS to whatever was focused before
 * it opened. Optionally wires Escape-to-close for consumers that don't already
 * own their key handling (the primitives Modal/Drawer keep their document-level
 * Escape listener, so they pass no onEscape).
 *
 * Self-contained — no provider. A dialog roots the trap on its container ref:
 *
 *   const ref = useFocusTrap(open);   // trap while open
 *   return <div ref={ref} role="dialog" aria-modal="true">…</div>;
 */
import * as React from "react";
import { pickInitialFocus, resolveOpener } from "@/lib/dialog.js";

// Everything natively tabbable, minus anything explicitly removed from the order.
const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

function focusableWithin(root) {
  if (!root) return [];
  // Skip anything hidden (display:none / aria-hidden) — offsetParent is null for
  // display:none subtrees, which keeps the cycle to what the user can actually reach.
  return Array.from(root.querySelectorAll(FOCUSABLE)).filter(
    (el) => el.offsetParent !== null || el === document.activeElement,
  );
}

/**
 * @param {boolean}   active   whether the trap is live (usually the modal's open flag)
 * @param {() => void} [onEscape] optional Escape handler (wired only while active)
 * @param {{ initialFocusRef?: React.RefObject<HTMLElement> }} [options]
 *   initialFocusRef — explicit initial target, wins over the §4.5 order
 * @returns {React.RefObject<HTMLElement>} attach to the modal container
 */
export function useFocusTrap(active = true, onEscape, options = {}) {
  // Held in a ref (like onEscape) so the open effect reads the latest target
  // without re-binding.
  const initialFocusHolder = React.useRef(options.initialFocusRef);
  React.useEffect(() => {
    initialFocusHolder.current = options.initialFocusRef;
  }, [options.initialFocusRef]);
  const containerRef = React.useRef(null);
  // Keep the latest onEscape in a ref so the keydown listener (bound once per open)
  // always calls the current handler without re-binding. Updated in an effect — never
  // written during render.
  const escapeRef = React.useRef(onEscape);
  React.useEffect(() => {
    escapeRef.current = onEscape;
  }, [onEscape]);

  React.useEffect(() => {
    if (!active) return undefined;
    const container = containerRef.current;
    if (!container) return undefined;

    // Remember where focus was so we can restore it on close.
    // (A child's `autoFocus` may already have moved focus inside: then the
    // opener is the last element focused outside any dialog.)
    const previouslyFocused = resolveOpener(container, document.activeElement);

    // Move focus inside on open (ux/01 §4.5, AUD-UXDS-07 / AUD-RECL-08): an
    // explicit initialFocusRef, then [data-autofocus] (React's `autoFocus`
    // renders NO DOM attribute, which is why the old `[autofocus]` lookup
    // fell through to the × button), a DOM [autofocus], the first field, the
    // primary action — never the × ([data-dialog-close]). Else the container.
    const initial =
      initialFocusHolder.current?.current || pickInitialFocus(container);
    if (initial && typeof initial.focus === "function") {
      initial.focus();
      // A text field opens ready to overtype its prefilled value.
      if (
        typeof initial.select === "function" &&
        initial.tagName === "INPUT" &&
        initial.hasAttribute("data-select-on-focus")
      ) {
        initial.select();
      }
    } else if (typeof container.focus === "function") {
      if (!container.hasAttribute("tabindex")) container.setAttribute("tabindex", "-1");
      container.focus();
    }

    const onKeyDown = (e) => {
      if (e.key === "Escape" && escapeRef.current) {
        e.stopPropagation();
        escapeRef.current();
        return;
      }
      if (e.key !== "Tab") return;
      const items = focusableWithin(container);
      if (items.length === 0) {
        // Nothing tabbable — keep focus pinned to the container.
        e.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const activeEl = document.activeElement;
      if (e.shiftKey) {
        if (activeEl === first || !container.contains(activeEl)) {
          e.preventDefault();
          last.focus();
        }
      } else if (activeEl === last || !container.contains(activeEl)) {
        e.preventDefault();
        first.focus();
      }
    };

    container.addEventListener("keydown", onKeyDown);
    return () => {
      container.removeEventListener("keydown", onKeyDown);
      // Restore focus to the opener when the modal closes/unmounts.
      if (previouslyFocused && document.contains(previouslyFocused)) {
        previouslyFocused.focus();
      }
    };
  }, [active]);

  return containerRef;
}

export default useFocusTrap;
